<template>
  <section class="section-checkout">
    <div class="row">
      <div class="col-lg-1 d-none d-lg-block">&nbsp;</div>
      <div class="col-lg-6 col-12">
        <!-- Repeat booking section -->
        <repeat-booking v-if="preCheckoutData && preBookingData && preBookingData.is_repeat_booking"></repeat-booking>
        <div id="checkout-content"
             :class="{'card shadow-sm': !$mq.phone}">
          <div :class="{'card-body px-1 py-3': !$mq.phone}">
            <div class="row">
              <div class="col-12 mt-2" :class="{'p-0': $mq.phone}">
                <h3 class="booking-title">{{ $t('message.booking.checkout_step.title') }}</h3>
                <!-- Section contact -->
                <section class="mt-4" v-if="preCheckoutData">
                  <h4>{{ $t('message.booking.checkout_step.customer') }}</h4>
                  <contact-form ref="contactForm" @savedLocation="clearLocationError" />
                  <div class="mt-2 text-remark -status-alert" v-if="errors.has('location')">
                    <i class="fas fa-exclamation-triangle"></i> {{ $t('message.required_pin_location') }}
                  </div>
                </section>
                <section v-else>
                  <loader/>
                </section>
                <section class="mt-4" v-if="preCheckoutData">
                  <h4>{{ $t('message.booking.checkout_step.tax_invoice') }}</h4>
                  <!-- Credit/Debit Card -->
                  <div class="card shadow-sm">
                    <div class="card-body">
                      <div class="exp">
                        <div class="checkbox">
                          <input type="checkbox"
                                 id="requestReceipt"
                                 v-model="preCheckoutData.request_receipt"
                                 name="requestReceipt"/>
                          <label for="requestReceipt" class="mb-0">
                            <span><!-- This span is needed to create the "checkbox" element --></span>
                            {{ $t('message.booking.checkout_step.request_tax_invoice') }}
                          </label>
                        </div>
                      </div>
                      <div v-if="preCheckoutData.request_receipt" class="mt-2">
                        <!-- No Companies -->
                        <template v-if="_.isEmpty(companies) || !preCheckoutData.user_company">
                          <button class="btn btn-light btn-block card-dashed mt-3 py-2 text-center"
                                  @click="openUserCompanyModal">
                            <i class="fas fa-plus-circle"></i> {{ $t('message.company_address.add_new_address_for_receipt') }}
                          </button>
                        </template>

                        <!-- Have Companies -->
                        <template v-else>
                          <div class="text-comment" v-if="preCheckoutData.user_company">
                            <div class="d-flex align-items-center justify-content-start mt-4 mb-2">
                              <div class="flex-fill">
                                <b>{{ preCheckoutData.user_company.company }}</b>
                              </div>
                              <div class="flex-fill text-right">
                                <b>{{ preCheckoutData.user_company.tel }}</b>
                              </div>
                            </div>
                            <div>{{ preCheckoutData.user_company.address }}</div>
                            <div class="mt-3 text-right">
                              <a href=""
                                 class="text-primary-link"
                                 @click.prevent="openUserCompanyModal">{{
                                $t('message.booking.checkout_step.edit_data') }}</a>
                            </div>
                          </div>
                        </template>
                        <input type="hidden"
                               name="userCompany"
                               id="userCompany"
                               v-validate="'required'"
                               v-if="preCheckoutData.request_receipt"
                               v-model="preCheckoutData.user_company">
                        <div class="mt-2 text-remark -status-alert" v-if="errors.has('userCompany')">
                          {{ $t('message.required_field') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Section customer photos -->
                <section class="mt-4" v-if="preCheckoutData && checkoutConditions">
                  <customer-area-photo :checkoutConditions="checkoutConditions" 
                                       @setAdditionalRemark="callbackAdditonalRemark" />
                </section>

                <!-- Section extras -->
                <section class="mt-4" v-if="preCheckoutData && preBookingData.service_id === 1 && displayExtras">
                  <extra-form :hasProfessional="false" />
                </section>

                <!-- Section has ironing board -->
                <section class="mt-4" v-if="preCheckoutData && globalState.configData.display_has_ironing_board && hasIroningService">
                  <has-ironing-board />
                </section>

                <!-- Section order summary -->
                <section class="d-lg-none" id="summary-booking-mobile">
                  <div class="card shadow-sm">
                    <div class="card-body">
                      <!-- Section summary booking -->
                      <summary-booking-mobile/>
                      <!-- Section checkout cost -->
                      <checkout-cost/>
                      <div class="text-remark mt-3">
                        {{ $t('message.booking.summary.all_inclusive_title') }}
                        {{ $t('message.booking.summary.all_inclusive_general_cleaning') }}
                      </div>
                    </div>
                  </div>
                </section>

                <!-- Section payment -->
                <section class="mt-4" v-if="preCheckoutData && totalPrice >= 20">
                  <h4 id="paymentMethod">{{ $t('message.booking.checkout_step.payment_method') }}</h4>
                  <!-- Credit/Debit Card -->
                  <div id="user-credit-cards" class="card shadow-sm">
                    <div class="card-body">
                      <div class="exp">
                        <div class="checkbox">
                          <input type="radio"
                                 id="paymentCreditDebit"
                                 name="paymentMethod"
                                 value="credit_card"
                                 v-model="preCheckoutData.payment_method"
                                 v-validate="'required'"/>
                          <label for="paymentCreditDebit" class="mb-0">
                            <span><!-- This span is needed to create the "checkbox" element --></span>
                            {{ $t('message.booking.payment_step.pay_by_credit_debit_card') }}
                          </label>
                        </div>
                      </div>
                      <div class="pt-2" v-if="preCheckoutData.payment_method === 'credit_card'">
                        <user-credit-cards :canEditCard="false"
                                           :action="'checkout_payment'"
                                           @onSelected="applyUserCreditCard"
                                           @onAddNewCard="addNewCreditCard"></user-credit-cards>
                      </div>
                    </div>
                  </div>
                  <!-- Bank transfer -->
                  <div class="card shadow-sm">
                    <div class="card-body">
                      <div class="exp">
                        <div class="checkbox">
                          <input type="radio"
                                 id="bankTransfer"
                                 ref="bankTransfer"
                                 name="paymentMethod"
                                 value="bank_transfer"
                                 v-model="preCheckoutData.payment_method"/>
                          <label for="bankTransfer" class="mb-0">
                            <span><!-- This span is needed to create the "checkbox" element --></span>
                            {{ $t('message.booking.payment_step.pay_by_bank_transfer') }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Promptpay -->
                  <div class="card shadow-sm">
                    <div class="card-body py-3">
                      <div class="exp" v-if="globalState.configData.promptpay.status === 1">
                        <div class="checkbox">
                          <input type="radio"
                                 id="promptpay"
                                 ref="promptpay"
                                 name="paymentMethod"
                                 value="promptpay"
                                 v-model="preCheckoutData.payment_method"/>
                          <label for="promptpay" class="mb-0">
                            <span><!-- This span is needed to create the "checkbox" element --></span>
                            {{ $t('message.booking.payment_step.pay_by_promptpay') }}
                            <img src="/static/img/thai_qr_payment.png" width="90" class="rounded ml-2"/>
                          </label>
                        </div>
                      </div>
                      <div v-else>
                        <div class="d-flex align-items-center justify-content-start">
                          <div>
                            <i class="fas fa-circle fa-lg -status-offline"></i>
                          </div>
                          <div class="pl-3">
                            {{ $t('message.booking.payment_step.pay_by_promptpay') }}
                          </div>
                          <div>
                            <img src="/static/img/thai_qr_payment.png" width="90" class="rounded ml-2"/>
                          </div>
                        </div>
                        <div class="pt-3 text-small">
                          {{ ($i18n.locale === 'th') ? globalState.configData.promptpay.remark_th : globalState.configData.promptpay.remark_en }}
                        </div>
                      </div>

                    </div>
                  </div>
                  <!-- Wallet -->
                  <div class="card shadow-sm"
                       v-if="preCheckoutData && preCheckoutData.wallet && Number(preCheckoutData.wallet.balance_amount) > 0 && !preBookingData.subscription_type_id">
                    <div class="card-body">
                      <div class="exp">
                        <div class="checkbox">
                          <input type="radio"
                                 id="wallet"
                                 name="paymentMethod"
                                 value="wallet"
                                 :disabled="!isAvailableBalance()"
                                 v-model="preCheckoutData.payment_method"/>
                          <label :class="{'text-secondary': !isAvailableBalance()}"
                                 for="wallet" class="mb-0">
                            <span><!-- This span is needed to create the "checkbox" element --></span>
                            {{ $t('message.booking.payment_step.pay_by_wallet') }}
                            <strong>{{ preCheckoutData.wallet.balance_amount | currency }}</strong>
                          </label>
                          <span class="text-error small" v-if="!isAvailableBalance()">
                            {{ $t('message.booking.payment_step.insufficient_balance') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="mt-2 text-remark -status-alert" v-if="errors.has('paymentMethod')">
                    <i class="fas fa-exclamation-triangle"></i> {{ $t('message.required_selection') }}
                  </div>
                </section>

               
                <!-- Section terms -->
                <terms-conditions/>

                <div class="mt-4">
                  <button :disabled="state.isAddingOrder || hasUnavailableServiceArea()"
                          class="btn btn-info btn-block btn-lg"
                          @click="submitBooking">
                    {{ $t(isAddingOrder) }}
                  </button>
                </div>
                <div class="py-4 pb-sm-0 text-center">
                  <a href="" @click.prevent="$router.go(-1)">
                    <i class="fas fa-angle-left"></i> {{ $t('message.booking.new_booking.back') }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
      <!-- /.section-contact -->
      <div class="col-lg-4 d-none d-lg-block">
        <!-- Summary Desktop -->
        <div v-sticky sticky-offset="{top: 120, bottom: 20}" >
          <summary-booking-desktop/>
        </div>
      </div>
      <div class="col-lg-1 d-none d-lg-block">&nbsp;</div>
    </div>
    <!-- Submitting Booking Modal -->
    <sweet-modal ref="submittingBookingModal" overlay-theme="dark" :hide-close-button="hideCloseButton"
                 :blocking="hideCloseButton">
      <loader/>
      {{ $t('message.booking.payment_step.sending_request') }}
    </sweet-modal>
    <company-address-modal ref="userCompanyModal" :manageData="true"/>
    <snackbar ref="snackbar"
              baseSize="100px"
              :colors="{open: '#333', info: '#3DBD7D', error: '#FA7377', warn: '#FF6600'}"
              :holdTime="2500"
              :position="'bottom-center'"/>
  </section>

</template>

<script>
  import { scroller } from 'vue-scrollto/src/scrollTo'
  import { checkoutCostMixin } from '../../mixins/checkoutCostMixin'
  import { orderMixin } from '../../mixins/orderMixin'
  import { mapGetters, mapState } from 'vuex'
  import { SweetModal } from 'sweet-modal-vue'
  import i18n from '../../helpers/i18nHelpers'
  import CheckoutCost from '../booking/components/CheckoutCost'
  import CompanyAddressForm from './components/CompanyAddressForm'
  import CompanyAddressModal from './components/CompanyAddressModal'
  import ContactForm from './components/ContactForm'
  import CustomerAreaPhoto from './components/CustomerAreaPhoto'
  import ExtraForm from './components/ExtraForm'
  import HasIroningBoard from './components/HasIroningBoard'
  import Loader from '../loader/Loader'
  import PromoCode from './components/PromoCode'
  import SummaryBookingDesktop from './components/SummaryBookingDesktop'
  import SummaryBookingMobile from './components/SummaryBookingMobile'
  import TermsConditions from './components/TermsConditions'
  import UserCreditCards from '../user/payment/UserCreditCards'
  import RepeatBooking from './RepeatBooking'
  import VueScrollTo from 'vue-scrollto'
  import packageHelpers from '../../helpers/packageHelpers'
  import subscriptionHelpers from '../../helpers/subscriptionHelpers'
  import userEventLogHelpers from '../../helpers/userEventLogHelpers'
  import insiderHelpers from '../../helpers/insiderHelpers'

  export default {
    mixins: [checkoutCostMixin, orderMixin],
    components: {
      CheckoutCost,
      CompanyAddressForm,
      CompanyAddressModal,
      ContactForm,
      CustomerAreaPhoto,
      ExtraForm,
      HasIroningBoard,
      Loader,
      PromoCode,
      SummaryBookingDesktop,
      SummaryBookingMobile,
      SweetModal,
      TermsConditions,
      UserCreditCards,
      RepeatBooking
    },
    data () {
      return {
        hideCloseButton: true,
        displayExtras: false,
        additionalRemark: null,
        checkoutConditions: null
      }
    },
    computed: {
      ...mapState('auth', ['isLineLoggedIn']),
      ...mapGetters({
        appliedPromoCode: 'promoCode/getAppliedPromoCode',
        appliedExtras: 'extras/getAppliedExtras',
        globalState: 'globalVal/getState',
        isAddingOrder: 'booking/isAddingOrder',
        newOrderData: 'booking/getNewOrderData',
        payment: 'payment/getPayment',
        preBookingData: 'booking/getPreBookingData',
        selectedProfessional: 'booking/getSelectedProfessional',
        state: 'booking/getState',
        user: 'auth/getUser',
        userPartner: 'partner/getUserPartner'
      }),
      ...mapState('booking', ['customerAreaPhotos', 'preCheckoutData', 'tmpBookingData', 'tmpPreCheckoutData']),
      ...mapState('companyAddress', ['companies']),
      ...mapState('payment', {isSavedCreditCard: state => state.isSaving}),
      ...mapState('promoCode', ['savedPromoCode']),
      hasIroningService () {
        let result = false
        if (this.preCheckoutData && ((this.preCheckoutData.parent_service_id && this.preCheckoutData.parent_service_id === 2) || (this.preBookingData.service_id === 1 && this.preCheckoutData.extra && this.preCheckoutData.extra.service_id === 2))) {
          result = true
        }
        return result
      }
    },
    watch: {
      appliedPromoCode () {
        if (this.appliedPromoCode) {
          if (this.appliedPromoCode.is_applied) {
            this.displaySnackbar(this.$i18n.t('message.promo_code.you_applied_promo_code'))
          } else if (this.appliedPromoCode.promo_code.payment_method && !this.appliedPromoCode.is_applied && this.appliedPromoCode.status === 'not_matched_payment_method') {
            this.displaySnackbar(this.$i18n.t('message.promo_code.not_matched_payment_method'))
          }
          // Check 100% discount
          if (this.appliedPromoCode.discount === 100 && this.appliedExtras) {
            this.displayExtras = false
            this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {key: 'extra', value: null})
            this.$store.commit('booking/UPDATE_PRE_BOOKING_DATA', {key: 'work_hour', value: this.preBookingData.init_work_hour})
            this.$store.commit('extras/SET_HAS_APPLIED_EXTRAS', false)
            this.calculateServiceCost()
          }
        } else {
          this.displayExtras = true
        }
      },
      isSavedCreditCard () {
        if (this.isSavedCreditCard) {
          setTimeout(() => { this.scrollToDiv() }, 500)
        }
      },
      totalPrice () {
        // Check discount 100% zero cost
        if (this.totalPrice <= 0) {
          this.preCheckoutData.payment_method = 'bank_transfer'
          this.preCheckoutData.omise_customer_id = null
        }
      },
      preCheckoutData () {
        if (this.user && this.preCheckoutData) {
          this.displayExtras = true
          const serviceId = this.preCheckoutData.parent_service_id ? this.preCheckoutData.parent_service_id : this.preBookingData.service_id
          const service = this.globalState.services[serviceId]
          if (service && service.checkout_conditions) {
            this.checkoutConditions = service.checkout_conditions
          }
          if (this.savedPromoCode) {
            this.applySavedPromoCode()
          }
          if (this.preCheckoutData.user_place) {
            if (this.preCheckoutData.user_place.pet_remark) {
              this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {
                key: 'pet_remark',
                value: this.preCheckoutData.user_place.pet_remark
              })
            }
            const latitude = this.preCheckoutData.user_place.latitude ? Number(this.preCheckoutData.user_place.latitude) : null
            const longitude = this.preCheckoutData.user_place.longitude ? Number(this.preCheckoutData.user_place.longitude) : null
            if ((this.preBookingData.latitude && this.preBookingData.longitude) && (!latitude && !longitude)) {
              const userPlace = { ...this.preCheckoutData.user_place }
              userPlace.latitude = this.preBookingData.latitude
              userPlace.longitude = this.preBookingData.longitude
              this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', { key: 'user_place', value: userPlace })
            }
          }

          // Apply subscription promo code
          if (this.preBookingData.subscription_type_id) {
            const subscriptionType = subscriptionHelpers.getSubscriptionTypeData(this.globalState.subscriptionTypes, this.preBookingData.subscription_type_id)
            const promoCode = subscriptionType.promo_code
            const submitData = {
              user_id: this.user.id,
              promo_code: promoCode,
              province_id: null,
              service_id: null,
              code_type: 'subscription'
            }
            this.$store.dispatch('promoCode/APPLY_PROMO_CODE', submitData)
          }

          // Apply package promo code
          if (this.preBookingData.package_type) {
            let promoCode = null
            // Check type of package
            switch (this.preBookingData.package_type.category) {
              case 'normal':
                promoCode = this.preBookingData.package_type.promo_code
                break
              case 'unlimited': {
                // Check case unlimited package
                const numDates = this.preBookingData.multi_dates_times.length
                const packageTypeDiscount = packageHelpers.getUnlimitedPackageDiscount(this.preBookingData.package_type, numDates)
                if (packageTypeDiscount) {
                  promoCode = packageTypeDiscount.promo_code
                }
              }
                break
              case 'flexible': {
                // Check case flexible package
                const packageTypeDiscount = this.preBookingData.package_type_discount
                promoCode = packageTypeDiscount.promo_code
              }
                break
            }
            // Apply promo code
            if (promoCode) {
              const submitData = {
                user_id: this.user.id,
                promo_code: promoCode,
                province_id: null,
                service_id: null,
                code_type: 'package'
              }
              this.$store.dispatch('promoCode/APPLY_PROMO_CODE', submitData)
            }
          }
        }
      },
      isAddingOrder () {
        // If complete adding order
        if (!this.state.isAddingOrder) {
          // if add order success
          if (this.newOrderData) {
            if (this.userPartner) {
              if (this.preCheckoutData.payment_method === 'credit_card') {
                const submitData = {
                  return_url: this.userPartner.return_url,
                  token: this.userPartner.token,
                  status: 'COMPLETED',
                  lang: this.$i18n.locale,
                  order_id: this.newOrderData.id.toString()
                }
                this.$store.dispatch('partner/SEND_COMPLETE_BOOKING', submitData)
              } else if (this.preCheckoutData.payment_method === 'bank_transfer') {
                const page = this.newOrderData.authorizeUri
                this.$router.push(page)
              }
            } else {
              // Set booking from in order to back to previous page correctly
              const bookingFrom = {
                from: 'chooseCleaner',
                professionalId: null
              }
              this.$store.commit('booking/SET_BOOKING_FROM', bookingFrom)

              switch (this.preCheckoutData.payment_method) {
                case 'credit_card':
                case 'wallet': {
                  // Redirect to booking from
                  const bookingFrom = this.state.bookingFrom
                  let page = ''
                  if (bookingFrom.from === 'cleanerDetail') {
                    page = '/cleaner/' + bookingFrom.professionalId
                  } else {
                    page = (this.isLineLoggedIn) ? '/line-liff' : '/thank-you'
                  }
                  // Clear pre booking data
                  this.$store.commit('booking/SET_PRE_BOOKING_DATA', null)
                  // Redirect to booking page
                  this.$router.push(page)
                }
                  break
                case 'bank_transfer': {
                  let page = this.newOrderData.authorizeUri
                  if (!this.totalPrice) {
                    page = (this.isLineLoggedIn) ? '/line-liff' : '/booking/start'
                  }
                  this.$router.push(page)
                }
                  break
                case 'promptpay': {
                  let page = `/orders/${this.newOrderData.id}/confirm-promptpay`
                  this.$router.push(page)
                }
                  break
              }
            }
          } else {
            // add order not success error payment
            this.$refs.submittingBookingModal.close()
            let alertText = this.$i18n.t('message.alert.cannot_perform_booking')
            switch (this.state.failureCode) {
              case 'invalid_security_code':
                alertText += this.$i18n.t('message.alert.payment_error_invalid_security_code')
                break
              case 'payment_rejected':
                alertText += this.$i18n.t('message.alert.payment_error_payment_rejected')
                break
              case 'insufficient_fund':
                alertText += this.$i18n.t('message.alert.payment_error_insufficient_fund')
                break
              case 'stolen_or_lost_card':
                alertText += this.$i18n.t('message.alert.payment_error_stolen_or_lost_card')
                break
              case 'failed_processing':
                alertText += this.$i18n.t('message.alert.payment_error_failed_processing')
                break
              case 'failed_fraud_check':
                alertText += this.$i18n.t('message.alert.payment_error_failed_fraud_check')
                break
              case 'invalid_account_number':
                alertText += this.$i18n.t('message.alert.payment_error_invalid_account_number')
                break
              case 'expiration date cannot be in the past':
                alertText += this.$i18n.t('message.alert.payment_error_expired_date')
                break
              case 'insufficient_credit':
                alertText = this.$i18n.t('message.alert.payment_error_insufficient_credit')
                break
              case 'invalid_promo_code_type':
                alertText = this.$i18n.t('message.alert.invalid_promo_code_type')
                break
              case 'expired_promo_code':
                alertText = this.$i18n.t('message.alert.expired_promo_code')
                break
              default:
                alertText = this.state.errorMessage
                break
            }
            this.$swal({text: alertText, type: 'error'})
          }
        }
      },
      'preCheckoutData.payment_method': function (newVal, oldVal) {
        if (newVal && newVal !== 'credit_card') {
          this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {key: 'omise_customer_id', value: null})
          if (this.appliedPromoCode && this.appliedPromoCode.promo_code.payment_method && this.appliedPromoCode.promo_code.payment_method !== newVal) {
            this.autoApplyPromoCode()
          }
        }
      },
      'preCheckoutData.omise_customer_id': function (newVal, oldVal) {
        if (newVal && this.appliedPromoCode && this.appliedPromoCode.promo_code.payment_method === 'credit_card') {
          this.autoApplyPromoCode()
        }
      }
    },
    methods: {
      applySavedPromoCode () {
        // Apply promo code for first time usage (WELCOME)
        const submitData = {
          user_id: this.user.id,
          promo_code: this.savedPromoCode.code,
          province_id: this.preBookingData.province_id,
          service_id: this.preBookingData.service_id,
          work_hour: this.preBookingData.init_work_hour
        }
        this.$store.dispatch('promoCode/APPLY_PROMO_CODE', submitData)
        // Clear saved promo code
        this.$store.commit('promoCode/SET_SAVED_PROMO_CODE', null)
      },
      clearLocationError () {
        this.$validator.errors.remove('location')
      },
      hasUnavailableServiceArea () {
        if (this.$refs.contactForm) {
          return this.$refs.contactForm.hasUnavailableServiceArea()
        }
        return false
      },
      callbackAdditonalRemark (value) {
        this.additionalRemark = value
      },
      scrollToDiv () {
        const options = {
          easing: 'ease-in-out',
          offset: -240,
          duration: 1000,
          cancelable: true,
          x: false,
          y: true
        }
        const targetElement = (this.$mq.desktop) ? '#user-credit-cards' : '#summary-booking-mobile'
        VueScrollTo.scrollTo(targetElement, 500, options)
      },
      addNewCreditCard () {
        const submitData = this.generateSubmitData()
        this.$store.commit('booking/SET_TMP_BOOKING_DATA', submitData)
        this.$store.commit('booking/SET_TMP_PRE_CHECKOUT_DATA', this.preCheckoutData)
      },
      applyUserCreditCard (userCreditCard) {
        this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {
          key: 'omise_customer_id',
          value: userCreditCard.omise_customer_id
        })
      },
      autoApplyPromoCode () {
        if (this.appliedPromoCode) {
          const submitData = {
            user_id: this.user.id,
            promo_code: this.appliedPromoCode.promo_code.code,
            province_id: this.preBookingData.province_id,
            service_id: this.preBookingData.service_id,
            work_hour: this.preBookingData.init_work_hour,
            payment_method: (this.preCheckoutData && this.preCheckoutData.payment_method) ? this.preCheckoutData.payment_method : null,
            omise_customer_id: (this.preCheckoutData && this.preCheckoutData.omise_customer_id) ? this.preCheckoutData.omise_customer_id : null
          }
          this.$store.dispatch('promoCode/APPLY_PROMO_CODE', submitData)
        }
      },
      alertWelcomePromotion () {
        let html = '<div class="mb-4">' + this.$i18n.t('message.booking.checkout_step.welcome_code_title') + '</div>'
        html += '<h2>' + this.$i18n.t('message.booking.checkout_step.welcome_code_detail') + '</h2>'
        this.$swal({
          html: html,
          showCancelButton: true,
          reverseButtons: true,
          confirmButtonText: this.$i18n.t('message.button.apply_promo_code_now'),
          cancelButtonText: this.$i18n.t('message.button.apply_promo_code_later')
        }).then((result) => {
          if (result) {
            // Apply promo code for first time usage (WELCOME)
            const submitData = {
              user_id: this.user.id,
              promo_code: 'WELCOME05',
              province_id: this.preBookingData.province_id,
              service_id: this.preBookingData.service_id,
              work_hour: this.preBookingData.init_work_hour
            }
            this.$store.dispatch('promoCode/APPLY_PROMO_CODE', submitData)
          }
        })
      },
      openUserCompanyModal () {
        // Fetch user company addresses
        const queryData = {user_id: this.user.id}
        this.$store.dispatch('companyAddress/LOAD_COMPANY_ADDRESS', queryData)
        this.$refs.userCompanyModal.showUserCompanyModal(true)
      },
      isAvailableBalance () {
        const walletBalance = Number(this.preCheckoutData.wallet.balance_amount)
        return (walletBalance >= this.totalPrice)
      },
      submitBooking () {
        // Check deactivated user account
        if (this.user.status === 2) {
          this.$swal({
            title: i18n.t('message.alert.suspended_title'),
            text: i18n.t('message.alert.suspended_detail')
          })
        } else {
          // Validate photo attachment case deep cleaning
          if (this.checkoutConditions && this.checkoutConditions.allow_photo_attachment && this.checkoutConditions.required_photo_attachment && this.customerAreaPhotos.length === 0) {
            this.$validator.errors.add({
              field: 'photoAttachment',
              msg: 'no photo attachment'
            })
          }
          // Validate user place location
          const latitude = this.preCheckoutData.user_place.latitude ? Number(this.preCheckoutData.user_place.latitude) : null
          const longitude = this.preCheckoutData.user_place.longitude ? Number(this.preCheckoutData.user_place.longitude) : null
          if (!latitude && !longitude) {
            this.$validator.errors.add({
              field: 'location',
              msg: 'no location'
            })
          }
          this.$validator.validate().then(async (valid) => {
            if (valid) {
              if (this.globalState.configData.checkout_acceptance) {
                let htmlContent = `<div class="text-center p-3">`
                htmlContent += `<div class="mb-4"><img src="/static/img/ic_wallet.png" class="img-fluid"></div>`
                htmlContent += `<div class="font-weight-bold mb-4">${this.$i18n.t('message.booking.checkout_step.booking_acceptance01')}</div>`
                htmlContent += `<div class="mb-4">${this.$i18n.t('message.booking.checkout_step.booking_acceptance02')}</div>`
                htmlContent += `<div class="text-info">${this.$i18n.t('message.booking.checkout_step.booking_acceptance03')}</div>`
                htmlContent += `</div>`
                this.$swal({
                  html: htmlContent,
                  confirmButtonColor: '#17a2b8',
                  confirmButtonText: this.$i18n.t('message.button.accept')
                }).then((result) => {
                  if (result) {
                    this.prePerformBooking()
                  }
                })
              } else {
                this.prePerformBooking()
              }
            } else {
              const elementId = `#${this.errors.items[0].field}`
              const scrollerTo = scroller()
              if (elementId === '#cleaningDate' || elementId === '#cleaningTime') {
                scrollerTo('body', 500)
              } else if (elementId !== '#acceptTerms') {
                scrollerTo(elementId)
              }
            }
          })
        }
      },
      async prePerformBooking () {
        // Save user place
        if (!this.preCheckoutData.user_place.id) {
          const userPlaceData = {
            user_id: this.user.id,
            place_name: 'My Home',
            place_size_id: this.preBookingData.place_size_id,
            work_hour: this.preBookingData.init_work_hour,
            province_id: this.preCheckoutData.province_id,
            address: this.preCheckoutData.address,
            latitude: this.preCheckoutData.user_place.latitude,
            longitude: this.preCheckoutData.user_place.longitude,
            remark: this.preCheckoutData.remark,
            has_pet: this.preCheckoutData.user_place.has_pet,
            pet_remark: this.preCheckoutData.user_place.pet_remark,
            address_province_id: this.preCheckoutData.address_province_id,
            district_id: this.preCheckoutData.district_id,
            sub_district_id: this.preCheckoutData.sub_district_id
          }
          await this.$store.dispatch('listing/UPDATE_USER_PLACE', userPlaceData)
        } else {
          // console.log('update user place')
          await this.$store.dispatch('listing/UPDATE_USER_PLACE', this.preCheckoutData.user_place)
        }
        // Check terms and conditions
        if (this.preCheckoutData.payment_method) {
          if (!this.state.acceptTermsConditions) {
            this.$swal({text: i18n.t('message.alert.check_conditions')})
          } else {
            switch (this.preCheckoutData.payment_method) {
              case 'credit_card':
                if (!this.preCheckoutData.omise_customer_id) {
                  this.$swal({text: i18n.t('message.alert.select_credit_card')})
                } else {
                  this.performBooking()
                }
                break
              case 'bank_transfer':
              case 'promptpay':
              case 'wallet':
                this.performBooking()
                break
              case null:
              case '':
                this.$swal({text: i18n.t('message.alert.select_payment_method')})
                break
            }
          }
        } else {
          this.$swal({text: i18n.t('message.alert.select_payment_method')})
        }
      },
      generateSubmitData () {
        // if new booking
        const latitude = this.preCheckoutData.user_place.latitude ? Number(this.preCheckoutData.user_place.latitude) : null
        const longitude = this.preCheckoutData.user_place.longitude ? Number(this.preCheckoutData.user_place.longitude) : null
        const submitData = {
          user_id: this.user.id,
          subscription_type_id: this.preBookingData.subscription_type_id,
          service_id: this.preBookingData.service_id,
          place_size_id: this.preBookingData.place_size_id,
          user_place_id: this.preCheckoutData.user_place.id,
          work_hour: Number(this.preBookingData.init_work_hour),
          cleaning_date: this.preBookingData.cleaning_date,
          cleaning_time: this.preBookingData.cleaning_time,
          email: this.user.email,
          name: this.preCheckoutData.name,
          tel: this.preCheckoutData.tel,
          address: this.preCheckoutData.address,
          remark: this.preCheckoutData.remark,
          latitude: latitude,
          longitude: longitude,
          province_id: this.preBookingData.province_id,
          omise_customer_id: this.preCheckoutData.omise_customer_id,
          selected_professional_id: (this.selectedProfessional) ? this.selectedProfessional.id : 0,
          total_price: 0,
          professional_income: 0,
          discount_cost: this.discountCost,
          credit_discount_cost: this.creditDiscountCost,
          service_vat: this.serviceVat,
          is_credit: this.preCheckoutData.is_credit ? 1 : 0,
          promo_code_id: 0,
          extras_id: 0,
          extra_helper: 1,
          language: this.$i18n.locale,
          platform: 'website',
          save_payment: 0,
          payment_method: this.preCheckoutData.payment_method,
          pet_remark: (this.preCheckoutData.user_place) ? this.preCheckoutData.user_place.pet_remark : null,
          request_receipt: this.preCheckoutData.request_receipt,
          user_company_id: null,
          address_province_id: this.preCheckoutData.address_province_id,
          district_id: this.preCheckoutData.district_id,
          sub_district_id: this.preCheckoutData.sub_district_id,
          require_vaccinated_professional: this.preCheckoutData.require_vaccinated_professional,
          is_new_cost_format: 1
        } // submitData

        // Calculate total price for tb_order (is_new_cost_format === 1)
        submitData.total_price = this.subtotal
        submitData.total_price_with_vat = this.totalPrice

        // Save page performing booking
        submitData.booking_uri = location.pathname + location.search

        // Additional remark
        if (this.additionalRemark) {
          submitData.remark = this.additionalRemark
        }

        // Experience id
        if (this.preBookingData.experience_id) {
          submitData.experience_id = this.preBookingData.experience_id
        }

        // User company id
        if (this.preCheckoutData.user_company && this.preCheckoutData.user_company.id) {
          submitData.user_company_id = this.preCheckoutData.user_company.id
        }

        // Check applying promo code
        if (this.appliedPromoCode) {
          submitData.applied_promo_code = this.appliedPromoCode
          submitData.promo_code_id = this.appliedPromoCode.id
        }

        // Check extras
        if (this.preCheckoutData && this.preCheckoutData.extra) {
          submitData.extras_id = this.preCheckoutData.extra.id
          submitData.work_hour += Number(this.preCheckoutData.extra.work_hour)
        }

        // Check package
        if (this.preBookingData.package_type) {
          submitData.package_type_id = this.preBookingData.package_type.id
          const category = this.preBookingData.package_type.category
          switch (category) {
            case 'unlimited':
              submitData.selected_dates_times = this.preBookingData.multi_dates_times
              break
            case 'flexible':
              submitData.package_type_discount_id = this.preBookingData.package_type_discount.id
              break
          }
        }

        // Check customer area photos
        if (this.customerAreaPhotos.length) {
          submitData.customer_area_photos = this.customerAreaPhotos
        }

        // Check service items
        if (this.preBookingData.service_items) {
          submitData.service_items = this.getServiceItemsSubmitData()
        }

        // Has ironing board
        if (this.preCheckoutData.has_ironing_board !== undefined) {
          submitData.has_ironing_board = this.preCheckoutData.has_ironing_board
        }
        return submitData
      },
      async performBooking () {
        if (this.preCheckoutData.payment_method) {
          this.$refs.submittingBookingModal.open()
          const submitData = this.generateSubmitData()
          // Check create subscription
          if (submitData.subscription_type_id) {
            await this.$store.dispatch('booking/ADD_SUBSCRIPTION', submitData)
          } else if (this.preBookingData.package_type) {
            await this.$store.dispatch('booking/ADD_PACKAGE', submitData)
          } else {
            await this.$store.dispatch('booking/ADD_ORDER', submitData)
          }
          // Clear customer area photos
          this.$store.commit('booking/CLEAR_CUSTOMER_AREA_PHOTOS')
        } else {
          this.$swal({text: i18n.t('message.alert.check_payment_method')})
        }
      },
      async fetchUserData () {
        // Fetch pre-checkout data including contact, address and payment
        if (this.user && this.preBookingData) {
          // Fetch payment data
          const submitData = {
            user_id: this.user.id,
            user_place_id: this.preBookingData.user_place_id,
            province_id: this.preBookingData.province_id,
            professional_id: (this.selectedProfessional) ? this.selectedProfessional.id : 0,
            service_id: this.preBookingData.service_id,
            work_hour: this.preBookingData.init_work_hour
          }
          // Check service items
          if (this.preBookingData.service_items) {
            submitData.service_items = this.getServiceItemsSubmitData()
          }
          // Fetch pre-checkout data
          await this.$store.dispatch('booking/LOAD_PRE_CHECKOUT_DATA', submitData)
          // Insider cart page event
          const params = {
            user: this.user,
            preBookingData: this.preBookingData,
            totalPrice: this.totalPrice
          }
          insiderHelpers.cartViewEvent(params)
        }
      },
      getServiceItemsSubmitData () {
        const serviceItems = []
        this.preBookingData.service_items.forEach(item => {
          serviceItems.push({ id: item.id, quantity: item.count })
        })
        return serviceItems
      },
      applyTmpCheckoutData () {
        if (this.tmpPreCheckoutData) {
          this.$store.commit('booking/SET_PRE_CHECKOUT_DATA', this.tmpPreCheckoutData)
        }
        if (this.tmpBookingData && this.tmpBookingData.applied_promo_code) {
          const promoCode = this.tmpBookingData.applied_promo_code.promo_code
          const submitData = {
            user_id: this.user.id,
            promo_code: promoCode.code,
            province_id: this.preBookingData.province_id,
            service_id: this.preBookingData.service_id,
            work_hour: this.preBookingData.init_work_hour,
            code_type: promoCode.code_type,
            payment_method: (this.preCheckoutData && this.preCheckoutData.payment_method) ? this.preCheckoutData.payment_method : null,
            omise_customer_id: (this.preCheckoutData && this.preCheckoutData.omise_customer_id) ? this.preCheckoutData.omise_customer_id : null
          }
          this.$store.dispatch('promoCode/APPLY_PROMO_CODE', submitData)
        }
      },
      displaySnackbar (message) {
        this.$refs.snackbar.open(message)
      }
    },
    mounted () {
      this.$store.commit('booking/SET_IS_ADDING_ORDER', false)
      this.$store.commit('booking/UPDATE_PRE_BOOKING_DATA', {
        key: 'work_hour',
        value: this.preBookingData.init_work_hour
      })

      if (this.user) {
        if (!this.tmpBookingData) {
          this.fetchUserData()
        } else {
          this.applyTmpCheckoutData()
        }
      } else {
        this.$router.push('/booking/start?step=1')
      }

      // New professional discount applied
      if (this.selectedProfessional && this.selectedProfessional.jobQty <= 0) {
        // Apply promo code for first time usage (NEWPROF)
        const submitData = {
          user_id: this.user.id,
          promo_code: 'NEWPROF',
          province_id: null,
          service_id: null,
          professional_id: this.selectedProfessional.id,
          code_type: 'new_prof'
        }
        this.$store.dispatch('promoCode/APPLY_PROMO_CODE', submitData)
      }
      // begin_booking
      const logData = {
        service_id: this.preBookingData.service_id,
        province_id: this.preBookingData.province_id,
        professional_id: this.selectedProfessional ? this.selectedProfessional.id : 0,
        service_date: this.selectedDate
      }
      if (this.preBookingData.init_work_hour) {
        logData.work_hour = this.preBookingData.init_work_hour
      }
      if (this.preBookingData.multi_dates_times) {
        logData.is_multi_datetime_bookings = true
      }
      if (this.preBookingData.experience_id) {
        logData.experience_id = this.preBookingData.experience_id
        this.$store.dispatch('experience/LOAD_EXPERIENCE_DETAIL', this.preBookingData.experience_id)
      }
      // Check extra for repeat booking
      if (this.preBookingData.is_repeat_booking && this.preBookingData.extra) {
        let html = `<strong>${this.$i18n.t('message.repeat_booking.modal_subtitle')}</strong><br>`
        html += `${this.$options.filters.multiLangName(this.preBookingData.extra)}<br><br>`
        html += this.$i18n.t('message.repeat_booking.modal_message')
        this.$swal({
          title: this.$i18n.t('message.repeat_booking.modal_title'),
          html: html,
          type: 'info'
        })
      }
      userEventLogHelpers.logEvent('begin_checkout', logData)
    },
    created () {
      this.$store.commit('booking/SET_NEW_ORDER_DATA', null)
      this.$store.commit('booking/SET_BOOKING_CURRENT_STEP', 3)
      this.$store.commit('promoCode/SET_APPLIED_PROMO_CODE', null)
      this.$store.commit('serviceRequest/SET_TMP_PAYMENT_CONFIRM_DATA', null)
      this.$store.commit('experience/SET_EXPERIENCE_DETAIL', null)
    },
    destroyed () {
      if (this.$refs.submittingBookingModal) {
        this.$refs.submittingBookingModal.close()
      }
    }
  }
</script>


// WEBPACK FOOTER //
// src/components/booking/CheckoutPage.vue